import { create, type StoreApi, type UseBoundStore } from "zustand";
import { getSocket } from "@/lib/socketClient";
import { onResync } from "@/lib/socketRecovery";
import { listNotificationsApi, markAllNotificationsReadApi, markNotificationReadApi } from "@/portals/patient/api/notificationsApi";
import { usePatientAuthStore } from "@/stores/patientAuthStore";

export type NotificationType =
  | "CAREGIVER_INVITE_RECEIVED"
  | "CAREGIVER_INVITE_ACCEPTED"
  | "CAREGIVER_INVITE_DECLINED"
  | "CAREGIVER_INVITE_CANCELLED"
  | "ACCESS_REQUEST_RECEIVED"
  | "ACCESS_REQUEST_APPROVED"
  | "ACCESS_REQUEST_DECLINED"
  | "CAREGIVER_ACCESS_REVOKED"
  | "CAREGIVER_ACCESS_UPDATED"
  | "ACCESS_CATEGORY_REQUESTED"
  | "ACCESS_CATEGORY_APPROVED"
  | "ACCESS_CATEGORY_DECLINED"
  | "CAREGIVER_ACCESS_EXPIRING_SOON"
  | "REVIEW_ITEM_NEEDS_INPUT"
  // Audience PATIENT (Module 5).
  | "REVIEW_ITEM_QUESTION"
  | "REVIEW_ITEM_RESOLVED"
  // Audience PATIENT (EMPI): an upload's patient record couldn't be confirmed
  // as this profile's person. Generic text only; opens Upload History.
  | "IDENTITY_UNCONFIRMED"
  // Audience ADMIN (Module 6).
  | "UNCLAIMED_ITEMS_ALERT"
  | "CLINICIAN_INVITE_DECLINED"
  | "CLINICIAN_INVITE_ACCEPTED"
  // Audience ADMIN: medication reviews just landed in the shared queue.
  | "REVIEW_QUEUE_ITEMS_ADDED"
  // Audience CLINICIAN (Module 4) — only ever served by /api/clinician/notifications.
  | "REVIEW_ITEM_ASSIGNED"
  | "REVIEW_ITEM_PATIENT_REPLIED"
  // Audience CLINICIAN: the patient said they no longer take it — closed by the system.
  | "REVIEW_ITEM_CLOSED_BY_PATIENT";

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data: {
    relationshipId?: string;
    categoryRequestId?: string;
    /** REVIEW_ITEM_NEEDS_INPUT: whose medications need input (self or an own dependent). */
    profileId?: string;
    count?: number;
    /** REVIEW_ITEM_ASSIGNED / _PATIENT_REPLIED (clinician) and REVIEW_ITEM_QUESTION / _RESOLVED (patient). */
    reviewItemId?: string;
    /** UNCLAIMED_ITEMS_ALERT (admin audience). */
    reviewItemIds?: string[];
    /** IDENTITY_UNCONFIRMED: the one upload it names, or (combined, several in one run) all of them. */
    uploadId?: string;
    uploadIds?: string[];
    /** CLINICIAN_INVITE_ACCEPTED / _DECLINED (admin audience). */
    invitationId?: string;
  } | null;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationsState {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  /** When the latest NEW unread notification arrived live (socket, or a later
   * refetch) — the bell plays its one-off "ring" cue on change. Never set by
   * the first load, so a page refresh doesn't ring. */
  lastArrivalAt: number | null;
  fetch: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Drops a notification from the list entirely — used once its inline
   * Approve/Deny action has resolved it, so it doesn't linger looking
   * actionable when it no longer is. */
  remove: (id: string) => void;
}

type ListResult = { data: { data: AppNotification[]; unreadCount: number } | null };

/** Everything that differs between one portal's notification feed and another's. */
export interface NotificationsStoreConfig {
  list: () => Promise<ListResult>;
  markRead: (id: string) => Promise<unknown>;
  markAllRead: () => Promise<unknown>;
  /** Registers the realtime "notification:new" listener for this portal's socket. */
  onIncoming: (handler: (incoming: AppNotification) => void) => void;
  /** Calls back with the portal's access token whenever it changes (null on logout). */
  onTokenChange: (handler: (token: string | null) => void) => void;
  /** Calls back when the portal's socket may have missed events (reconnect / server resync). */
  onResync?: (handler: () => void) => void;
}

export type NotificationsStore = UseBoundStore<StoreApi<NotificationsState>>;

/**
 * One portal's notification feed. Global, un-scoped like the auth stores (not a
 * React Context) — a portal's bell needs the same live state wherever its header
 * mounts it. Each portal gets its own instance (patient below; clinician in
 * clinicianNotificationsStore.ts), so their feeds can never mix.
 */
/** The user a JWT belongs to ("sub"), read without verifying (only compared, never trusted). */
function tokenSubject(token: string | null): string | null {
  try {
    return token ? (JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).sub ?? null) : null;
  } catch {
    return null;
  }
}

export function createNotificationsStore(config: NotificationsStoreConfig): NotificationsStore {
  return create<NotificationsState>((set, get) => {
    // A list request can be slow (seconds). Whatever arrives live while one is
    // in flight isn't in its answer, so the answer must not wipe it — and an
    // older answer landing after a newer one is ignored.
    let fetchSeq = 0;
    let liveSeq = 0;
    // Focus, reconnect and socket resync can all ask at once: callers share the
    // request in flight, and asks made meanwhile become ONE follow-up request.
    let inFlight: Promise<void> | null = null;
    let askedAgain = false;
    let session = 0; // moves on at sign-out and user switch
    const liveArrivals = new Map<string, { notification: AppNotification; seq: number }>();

    config.onIncoming((incoming) => {
      liveArrivals.set(incoming.id, { notification: incoming, seq: ++liveSeq });
      set((state) => {
        // A refetch can already have brought this one in — never list it twice.
        if (state.notifications.some((n) => n.id === incoming.id)) return state;
        return {
          notifications: [incoming, ...state.notifications],
          unreadCount: state.unreadCount + (incoming.isRead ? 0 : 1),
          ...(incoming.isRead ? {} : { lastArrivalAt: Date.now() }),
        };
      });
    });

    let lastToken: string | null = null;
    // The first load after sign-in is a baseline, not an "arrival".
    let loadedOnce = false;
    // Events sent while the socket was down are never replayed: refetch the list.
    config.onResync?.(() => {
      if (lastToken) void get().fetch();
    });
    config.onTokenChange((token) => {
      if (token === lastToken) return;
      // A new session or a different user (never trust a cached list across users).
      const signingIn = !lastToken || tokenSubject(lastToken) !== tokenSubject(token);
      lastToken = token;
      if (token) {
        // Only at sign-in: a silent token refresh mid-session changed the token
        // too and refetched the whole list every few minutes, while the socket
        // already keeps it live (connection plan step 4).
        if (signingIn) {
          // Never share a request still out for the previous person.
          fetchSeq++;
          inFlight = null;
          askedAgain = false;
          session++;
          void get().fetch();
        }
      } else {
        loadedOnce = false;
        liveArrivals.clear();
        fetchSeq++; // a request still in flight belongs to the old session
        inFlight = null;
        askedAgain = false;
        session++;
        set({ notifications: [], unreadCount: 0, lastArrivalAt: null });
      }
    });

    // One list request; its answer is merged with what arrived live meanwhile.
    const load = async () => {
      const seq = ++fetchSeq;
      const liveMark = liveSeq;
      set({ isLoading: true });
      const { data } = await config.list();
      if (seq !== fetchSeq) return; // a newer request is on its way; its answer wins
      const fetched = data?.data ?? [];
      const fetchedIds = new Set(fetched.map((n) => n.id));
      // Live arrivals newer than this request's start, not in its answer: kept
      // as they are now (maybe marked read meanwhile; gone if removed meanwhile).
      const current = new Map(get().notifications.map((n) => [n.id, n]));
      const kept = [...liveArrivals.values()]
        .filter((a) => a.seq > liveMark && !fetchedIds.has(a.notification.id))
        .flatMap((a) => current.get(a.notification.id) ?? []);
      for (const [id, a] of liveArrivals) if (a.seq <= liveMark || fetchedIds.has(id)) liveArrivals.delete(id);
      const next = [...kept, ...fetched];
      const known = new Set(get().notifications.map((n) => n.id));
      const arrived = loadedOnce && fetched.some((n) => !n.isRead && !known.has(n.id));
      if (data) loadedOnce = true;
      set({
        notifications: next,
        unreadCount: (data?.unreadCount ?? 0) + kept.filter((n) => !n.isRead).length,
        isLoading: false,
        ...(arrived ? { lastArrivalAt: Date.now() } : {}),
      });
    };

    return {
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      lastArrivalAt: null,

      fetch: () => {
        if (inFlight) {
          askedAgain = true;
          return inFlight;
        }
        const mine = session;
        const run = (async () => {
          do {
            askedAgain = false;
            await load();
          } while (askedAgain && session === mine);
        })().finally(() => {
          if (inFlight === run) inFlight = null;
        });
        inFlight = run;
        return run;
      },

      markRead: async (id: string) => {
        const target = get().notifications.find((n) => n.id === id);
        if (!target || target.isRead) return;
        set((state) => ({
          notifications: state.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
        await config.markRead(id);
      },

      markAllRead: async () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
          unreadCount: 0,
        }));
        await config.markAllRead();
      },

      remove: (id: string) => {
        set((state) => {
          const target = state.notifications.find((n) => n.id === id);
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: Math.max(0, state.unreadCount - (target && !target.isRead ? 1 : 0)),
          };
        });
      },
    };
  });
}

/**
 * The patient/caregiver feed (same account in both portals) — patient socket,
 * patient session, /api/notifications.
 */
export const useNotificationsStore = createNotificationsStore({
  list: listNotificationsApi,
  markRead: markNotificationReadApi,
  markAllRead: markAllNotificationsReadApi,
  onIncoming: (handler) => {
    getSocket().on("notification:new", handler);
  },
  onTokenChange: (handler) => {
    usePatientAuthStore.subscribe((state) => handler(state.accessToken));
  },
  onResync: (handler) => {
    onResync(getSocket(), handler);
  },
});
